import React from 'react';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ru from 'date-fns/locale/ru';

import Button from 'components/Button/Button';
import FormGroup from 'components/FormGroup/FormGroup';
import FormItem from 'components/FormItem/FormItem';
import Input from 'components/Input/Input';
import Panel from 'components/Panel/Panel';
import PanelHeader from 'components/PanelHeader/PanelHeader';

import WithProcessError from '../../hocs/WithProcessError';
import AddMember from './AddMember';
import RolesInfo from './RolesInfo';
import TeamMember from './TeamMember';
import Terminal from './Terminal';
import TerminalSettings from './TerminalSettings';

import Add24 from 'icons/Add24';

import useApi from 'utils/api';
import { useAuth } from 'utils/auth';
import classNames from 'utils/classNames';
import download from 'utils/download';
import { actions } from 'utils/permissions';
import { validateID, validateName } from 'utils/validate';

import 'react-datepicker/dist/react-datepicker.css';
import styles from './settings.scss';

function Settings(props) {
    const auth = useAuth();
    const api = useApi();
    const { t } = useTranslation();

    const { processError } = props;

    const queryClient = useQueryClient();

    const companyId = auth?.company.id;

    const [yookassaTerminalId, setYookassaTerminalId] = React.useState(null);

    const { isLoading, isFetching, data } = useQuery({
        queryKey: ['settings'],
        queryFn: getSettings,
        refetchOnWindowFocus: false,
        retry: 0,
        onSuccess: (data) => {
            const { url = '', name, users = [], offer_link = '' } = data;

            setCompanyName((companyName) => ({
                ...companyName,
                value: name,
                originalName: name,
            }));

            setCompanyURL((shortUrl) => ({
                ...shortUrl,
                value: url,
                originalUrl: url,
            }));

            setOfferLink((offerLink) => ({
                ...offerLink,
                value: offer_link || '',
                originalOfferLink: offer_link || '',
            }));

            setUsers(users);
        },
        onError: (error) => {
            props.processError('Не удалось загрузить настройки.', error);
        },
        initialData: {
            name: '',
            url: '',
            users: [],
            offer_link: '',
        },
    });

    const { data: paymentServices, isLoading: isPaymentServicesLoading } = useQuery({
        queryKey: ['payment-services', companyId],
        refetchOnWindowFocus: false,
        retry: 0,
        onSuccess: (data) => {
            /**
             * If Yookassa terminal specified,
             * try to get information about it's status.
             */
            const { payment_services } = data;
            const yookassa = payment_services.find((ps) => ps.type.toLowerCase() === 'yookassa');

            if (yookassa && yookassa.terminal_id) {
                setYookassaTerminalId(yookassa.terminal_id);
            }
        },
        queryFn: () => api.getPaymentServices(companyId),
    });

    const { data: yookassaTerminalData } = useQuery({
        queryKey: ['terminal-settings', companyId, yookassaTerminalId],
        queryFn: () => api.getTerminal(companyId, yookassaTerminalId),
        refetchOnWindowFocus: false,
        enabled: !!yookassaTerminalId,
    });

    const settingsMutation = useMutation({
        mutationFn: saveSettings,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['settings'] });
        },
    });

    const [companyName, setCompanyName] = React.useState({
        value: data.name,
        originalName: data.name,
        error: null,
    });
    const [companyURL, setCompanyURL] = React.useState({
        value: data.url,
        originalUrl: data.url,
        error: null,
    });
    const [offerLink, setOfferLink] = React.useState({
        value: data.offer_link,
        originalOfferLink: data.offer_link,
        error: null,
    });
    const [users, setUsers] = React.useState([]);

    const [startDate, setStartDate] = React.useState(new Date());
    const [endDate, setEndDate] = React.useState(new Date());

    const [terminalSetup, setTerminalSetup] = React.useState(null);
    const [sidePanel, setSidePanel] = React.useState(null);
    const [popup, setPopup] = React.useState(null);

    function getSettings() {
        const { id } = auth.company;

        return api.getCompanySettings(id);
    }

    async function saveSettings() {
        const settings = {
            name: companyName.value,
            url: companyURL.value || undefined,
            offer_link: offerLink.value,
        };

        return await api.updateCompanySetting(auth.company.id, settings);
    }

    function handleRolesInfo(e) {
        e.preventDefault();
        setPopup(<RolesInfo onClosed={handlePopupClose} />);
    }

    function handleCompanyNameChange(e) {
        const value = e.target.value;
        const error = validateName(value);
        setCompanyName((name) => ({ ...name, value, error }));
    }

    function handleCompanyURLChange(e) {
        const value = e.target.value;
        const error = validateID(value);
        setCompanyURL((url) => ({ ...url, value, error }));
    }

    function handleOfferLinkChange(e) {
        const value = e.target.value;
        setOfferLink((link) => ({ ...link, value }));
    }

    function onUserAdded() {
        settingsMutation.mutate();
        setSidePanel(null);
    }

    function onUserDeleted() {
        settingsMutation.mutate();
    }

    function handleAddMember() {
        if (terminalSetup !== null) {
            handleTerminalSetupClose();
        }

        setSidePanel(<AddMember onCancel={handleCancelAddMember} onUserAdded={onUserAdded} />);
    }

    function handleCancelAddMember() {
        setSidePanel(null);
    }

    function handlePopupClose() {
        setPopup(null);
    }

    function handleTerminalSetup(terminalId, serviceId) {
        setTerminalSetup(terminalId);
        setSidePanel(
            <TerminalSettings
                terminalId={terminalId}
                serviceId={serviceId}
                onSetup={handleTerminalSetup}
                onClose={handleTerminalSetupClose}
            />
        );
    }

    function handleTerminalSetupClose() {
        setTerminalSetup(null);
        setSidePanel(null);
    }

    function canSaveCompanyUpdates() {
        const noErrors = companyName.error === null && companyURL.error === null;
        const hasChanges =
            companyName.value !== companyName.originalName ||
            companyURL.value !== companyURL.originalUrl ||
            offerLink.value !== offerLink.originalOfferLink;

        return noErrors && hasChanges;
    }

    function canSaveRecurrentDocuments() {
        return offerLink.value !== offerLink.originalOfferLink;
    }

    function handleSaveCompanyInfo() {
        settingsMutation.mutate();
    }

    /**
     * Create sales report based on backend analytics.
     */
    function handleCreateReportClick() {
        const companyId = auth.company.id;

        api.exportAnalytics(companyId, startDate, endDate)
            .then((response) => {
                if (response.status === 200) {
                    response.blob().then((blob) => {
                        download(blob, 'dispurse_analytics.csv');
                    });
                } else {
                    processError('Не удалось собрать статистику за данный период', {});
                }
            })
            .catch((error) => {
                if (error.status === 400) {
                    processError('За данный период нет статистики.', {});
                }
            });
    }

    function prepareMembersToRender() {
        return users.map((member) => {
            return <TeamMember key={member.id} memberData={member} onDeleted={onUserDeleted} />;
        });
    }

    function prepareYookassaStatus() {
        if (yookassaTerminalData) {
            switch (yookassaTerminalData.registration_status) {
                case 'WAITING_FOR_TEST':
                    return (
                        <FormItem>
                            <p className={styles.yookassaNote}>
                                Ваш терминал Юкасса почти готов. Осталось совершить пробный платёж в настройках.
                            </p>
                        </FormItem>
                    );

                case 'REJECTED':
                    return (
                        <FormItem>
                            <p className={classNames(styles.yookassaNote, { [styles.error]: true })}>
                                Ваш терминал Юкасса отклонён. Нажмите «Настроить», чтобы узнать подробности.
                            </p>
                        </FormItem>
                    );

                default:
                    return null;
            }
        }

        return null;
    }

    const membersList = prepareMembersToRender();
    const paymentServicesList = paymentServices?.payment_services || [];
    const isLoadingData = () => isLoading || settingsMutation.isLoading;

    const yookassaStatus = prepareYookassaStatus();

    return (
        <>
            {popup}
            <Panel fill={true} header={<PanelHeader title={t('place.settings')} />}>
                <div className={styles.wrapper}>
                    <FormGroup title={t('settings.general.title')}>
                        <FormItem label={t('settings.general.companyName')} required={true} error={companyName.error}>
                            <Input
                                type="text"
                                name="company-name"
                                value={companyName.value}
                                disabled={auth.hasPermission(actions.EDIT_SETTINGS) === false || isLoadingData()}
                                onChange={handleCompanyNameChange}
                            />
                        </FormItem>
                        <FormItem label={t('settings.general.shortUrl')} error={companyURL.error}>
                            <Input
                                type="text"
                                name="company-short-url"
                                value={companyURL.value}
                                disabled={auth.hasPermission(actions.EDIT_SETTINGS) === false || isLoadingData()}
                                onChange={handleCompanyURLChange}
                            />
                        </FormItem>
                        <FormItem>
                            <Button
                                text={t('settings.general.saveChanges')}
                                needPermission={actions.EDIT_SETTINGS}
                                level={'secondary'}
                                disabled={!canSaveCompanyUpdates()}
                                onClick={handleSaveCompanyInfo}
                                loading={isLoadingData()}
                            />
                        </FormItem>
                    </FormGroup>

                    <FormGroup title={t('settings.team.title')}>
                        <p className={styles.teamDescription}>
                            {t('settings.team.description')}{' '}
                            <a href="#roles" onClick={handleRolesInfo}>
                                {t('settings.team.roleInCompany')}
                            </a>
                            .
                        </p>

                        <div className={styles.usersList}>{membersList}</div>

                        <FormItem>
                            <Button
                                level={'secondary'}
                                text={t('settings.team.addMember')}
                                onClick={handleAddMember}
                                size="m"
                                loading={isFetching}
                                icon={<Add24 />}
                            />
                        </FormItem>
                    </FormGroup>

                    {auth.hasPermission(actions.EDIT_TERMINAL) && (
                        <FormGroup title={t('settings.terminals.title')}>
                            {!isPaymentServicesLoading &&
                                paymentServicesList.map((service) => (
                                    <Terminal
                                        key={service.id}
                                        id={service.terminal_id}
                                        serviceId={service.id}
                                        title={'Платёжный терминал'}
                                        type={service.type}
                                        description={'Обычные платежи и подписки'}
                                        onSetup={handleTerminalSetup}
                                    />
                                ))}

                            {yookassaStatus}
                        </FormGroup>
                    )}

                    <FormGroup title={'Оферта рекуррентных платежей'}>
                        <p className={styles.teamDescription}>
                            Необходимо заполнить, если вы собираетесь продавать подписки. Пример оферты можно найти{' '}
                            <a href="https://disk.yandex.ru/i/-spH2YSeg6Arhg" target="_blank" rel="noreferrer">
                                здесь
                            </a>
                            .
                        </p>
                        <FormItem
                            caption={'Вставьте ссылку на документ (Google Drive,  Яндекс.Диск и тп.)'}
                            error={offerLink.error}
                        >
                            <Input
                                type="text"
                                name="offer_link"
                                value={offerLink.value}
                                onChange={handleOfferLinkChange}
                            />
                        </FormItem>
                        <FormItem>
                            <Button
                                text={t('settings.general.saveChanges')}
                                needPermission={actions.EDIT_SETTINGS}
                                level={'secondary'}
                                disabled={!canSaveRecurrentDocuments()}
                                onClick={handleSaveCompanyInfo}
                                loading={isLoadingData()}
                            />
                        </FormItem>
                    </FormGroup>

                    <FormGroup title={'Аналитика'}>
                        <p className={styles.teamDescription}>
                            Вы можете выгрузить статистику продаж за любой период в формате CSV.
                        </p>
                        <div style={{ display: 'flex' }}>
                            <FormItem label={'Начало отчета'} style={{ maxWidth: 220 }}>
                                <DatePicker
                                    selected={startDate}
                                    locale={ru}
                                    onChange={(date) => setStartDate(date)}
                                    className={'form-field'}
                                />
                            </FormItem>
                            <FormItem label={'Конец отчета'} style={{ maxWidth: 220 }}>
                                <DatePicker
                                    locale={ru}
                                    selected={endDate}
                                    onChange={(date) => setEndDate(date)}
                                    className={'form-field'}
                                />
                            </FormItem>
                        </div>

                        <FormItem>
                            <Button
                                text={'Создать отчёт'}
                                needPermission={actions.EDIT_SETTINGS}
                                level={'secondary'}
                                onClick={handleCreateReportClick}
                                loading={isLoadingData()}
                            />
                        </FormItem>
                    </FormGroup>
                </div>
            </Panel>
            {sidePanel}
        </>
    );
}

Settings.propTypes = {
    ...WithProcessError.propTypes,
};

export default WithProcessError(Settings);
